import { useEffect, useState } from "react";
import LText from "@components/LText";
import LBox from "@components/LBox";
import { Image, Dimensions } from "react-native";
import { fonts } from "@theme";
import { router } from "expo-router";
import { trackOnb } from "@components/Onboarding/analytics/trackOnboarding";
import { useOnboardingStore } from "@components/Onboarding/store/onboardingStore";
import { computeAha2Values } from "@components/Onboarding/Intro/ahaConfig";
import IntroLayout from "@components/Onboarding/Intro/IntroLayout";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  Easing,
} from "react-native-reanimated";

const bgImage = require("@assets/onboarding/aha2.webp");
const SCREEN_W = Dimensions.get("window").width;
const ANIM_DURATION = 500;
const STEP_DELAY = 600;
const EASING = Easing.out(Easing.cubic);

export default function Aha2() {
  const listenerType = useOnboardingStore((s) => s.quizListenerType);
  const yearsStreaming = useOnboardingStore((s) => s.quizYearsStreaming);
  const type = listenerType ?? "music-lover";
  const years = yearsStreaming ?? 2;
  const { yearlyAmount, totalLost } = computeAha2Values(type, years);

  const [step, setStep] = useState(0);

  useEffect(() => {
    trackOnb("signup", "aha-2", "view");
    const t1 = setTimeout(() => setStep(1), 200);
    const t2 = setTimeout(() => setStep(2), 200 + STEP_DELAY);
    const t3 = setTimeout(() => setStep(3), 200 + STEP_DELAY * 2);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const segO = useSharedValue(0);
  const segY = useSharedValue(40);
  const hlO = useSharedValue(0);
  const hlY = useSharedValue(30);
  const bodyO = useSharedValue(0);
  const bodyY = useSharedValue(20);

  useEffect(() => {
    if (step >= 1) {
      segO.value = withTiming(1, { duration: ANIM_DURATION, easing: EASING });
      segY.value = withTiming(0, { duration: ANIM_DURATION, easing: EASING });
    }
    if (step >= 2) {
      hlO.value = withTiming(1, { duration: ANIM_DURATION, easing: EASING });
      hlY.value = withTiming(0, { duration: ANIM_DURATION, easing: EASING });
    }
    if (step >= 3) {
      bodyO.value = withTiming(1, { duration: ANIM_DURATION, easing: EASING });
      bodyY.value = withTiming(0, { duration: ANIM_DURATION, easing: EASING });
    }
  }, [step, segO, segY, hlO, hlY, bodyO, bodyY]);

  const segStyle = useAnimatedStyle(() => ({
    opacity: segO.value,
    transform: [{ translateY: segY.value }],
  }));
  const hlStyle = useAnimatedStyle(() => ({
    opacity: hlO.value,
    transform: [{ translateY: hlY.value }],
  }));
  const bodyStyle = useAnimatedStyle(() => ({
    opacity: bodyO.value,
    transform: [{ translateY: bodyY.value }],
  }));

  return (
    <IntroLayout
      totalSteps={4}
      currentStep={2}
      buttonLabel="Damn, give me the good news"
      buttonEventName="aha_2"
      onButtonPress={() => router.navigate("/onboarding/aha-3")}
    >
      <Image
        source={bgImage}
        style={{
          position: "absolute",
          left: -16,
          top: "55%",
          width: SCREEN_W * 0.65,
          aspectRatio: 537 / 1131,
          transform: [{ translateY: "-50%" }],
        }}
        resizeMode="contain"
      />

      <LBox flex={1} justifyContent="center" alignItems="center">
        <Animated.View
          style={[
            segStyle,
            {
              marginBottom: 36,
              width: "110%",
            },
          ]}
        >
          <LText
            variant="Heading/H1/Medium"
            textAlign="center"
            color="white"
            style={{
              textShadowColor: "rgba(0,0,0,0.6)",
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: 4,
            }}
          >
            {"The bad news: you\u2019ve spent "}
            <LText
              variant="Heading/H1/Medium"
              style={{
                color: "#E85D4A",
                textShadowColor: "rgba(0,0,0,0.6)",
                textShadowOffset: { width: 0, height: 1 },
                textShadowRadius: 4,
              }}
            >
              {yearlyAmount}
            </LText>
            {
              " on artists you didn\u2019t stream last year. Meaning you have already \u201Clost\u201D"
            }
          </LText>
        </Animated.View>

        <Animated.View style={hlStyle}>
          <LText
            fontSize={80}
            fontFamily={fonts.extraBold}
            textAlign="center"
            lineHeight={80}
            style={{
              color: "#E85D4A",
              letterSpacing: -0.03 * 80,
              textShadowColor: "rgba(0,0,0,0.8)",
              textShadowOffset: { width: 0, height: 2 },
              textShadowRadius: 8,
            }}
          >
            {totalLost}
          </LText>
        </Animated.View>

        <Animated.View style={[bodyStyle, { marginTop: 36, width: "100%" }]}>
          <LText
            variant="Heading/H1/Medium"
            textAlign="center"
            color="white"
            style={{
              textShadowColor: "rgba(0,0,0,0.6)",
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: 4,
            }}
          >
            since you started streaming music.
          </LText>
        </Animated.View>
      </LBox>

      <LText
        fontSize={13}
        fontFamily={fonts.regular}
        lineHeight={18}
        textAlign="center"
        color="whiteTranslucent60"
        mb="s3"
      >
        {
          "Projection of your streaming royalties allocation,\nbased on your daily music consumption."
        }
      </LText>
    </IntroLayout>
  );
}
